export type ImageFormat = 'image/png' | 'image/jpeg';

export interface ImageOperation {
  readonly type: 'resize';
  readonly width: number;
  readonly height: number;
}

export interface ImageRequest {
  readonly bytes: ArrayBuffer;
  readonly inputFormat: ImageFormat;
  readonly outputFormat: ImageFormat;
  readonly operation: ImageOperation;
}

export interface ImageResult {
  readonly bytes: ArrayBuffer;
  readonly format: ImageFormat;
  readonly width: number;
  readonly height: number;
}

export type ImageProcessingErrorCode =
  | 'unsupported-request'
  | 'worker-unavailable'
  | 'worker-failed'
  | 'processing-failed'
  | 'input-too-large'
  | 'invalid-dimensions'
  | 'pixel-limit-exceeded'
  | 'unsupported-format'
  | 'decode-failed'
  | 'encode-failed'
  | 'invalid-quality';

export class ImageProcessingError extends Error {
  constructor(
    readonly code: ImageProcessingErrorCode,
    message: string,
    cause?: unknown
  ) {
    super(message, { cause });
    this.name = 'ImageProcessingError';
  }
}

export interface ImageWorkerRequest {
  readonly id: number;
  readonly request: ImageRequest;
}

export type ImageWorkerResponse =
  | { readonly id: number; readonly result: ImageResult }
  | {
      readonly id: number;
      readonly error: {
        readonly code: ImageProcessingErrorCode;
        readonly message: string;
      };
    };

const supportsFormat = (format: ImageFormat): boolean => format === 'image/png' || format === 'image/jpeg';
const MAX_INPUT_BYTES = 10 * 1024 * 1024;
const MAX_DIMENSION = 8192;
const MAX_PIXELS = 16_000_000;

export const supportsImageRequest = (request: ImageRequest): boolean =>
  request.bytes instanceof ArrayBuffer &&
  request.bytes.byteLength > 0 &&
  request.bytes.byteLength <= MAX_INPUT_BYTES &&
  supportsFormat(request.inputFormat) &&
  supportsFormat(request.outputFormat) &&
  request.operation?.type === 'resize' &&
  Number.isInteger(request.operation.width) &&
  request.operation.width > 0 &&
  request.operation.width <= MAX_DIMENSION &&
  Number.isInteger(request.operation.height) &&
  request.operation.height > 0 &&
  request.operation.height <= MAX_DIMENSION &&
  request.operation.width * request.operation.height <= MAX_PIXELS;
